import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { apiUrl, OTP_API_URL, getSelectedBranchId, getStoredAdmin } from '../config/api';
import { PAYMENT_ACCOUNT_NAME } from '../config/branding';

const HISTORY_API_URL     = apiUrl('credit_customer_history');
const SETTLEMENTS_API_URL = apiUrl('credit_settlements');
const REMINDERS_API_URL   = apiUrl('credit_reminders');
const CLEAR_CREDIT_API_URL = apiUrl('credit_clear_customer');
const CLOSE_CYCLE_URL     = apiUrl('credit_close');
const SALES_API_URL       = apiUrl('sales_management');
const SMS_API_URL         = apiUrl('send_sms');
const CREDIT_PAYMENT_LINK_API_URL = apiUrl('credit_payment_link');

const fmt = (n) => Number(n || 0).toFixed(2);
const displayInvoiceRef = (value, fallback = 'Invoice') => {
  const text = String(value || '').trim();
  return text || fallback;
};
const isCredit = (s) => String(s || '').toLowerCase() === 'credit';
const paymentMethodOptions = [
  { value: '', label: 'Not Specified' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'online', label: 'Online' },
];

function StatCard({ label, value, color, helper }) {
  return (
    <Paper
      sx={{
        p: 2,
        minWidth: 220,
        flex: '1 1 220px',
        borderRadius: 2,
        borderTop: 4,
        borderColor: color || 'primary.main',
        boxShadow: '0 8px 20px rgba(15,23,42,0.06)',
      }}
    >
      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800 }} color={color || 'text.primary'}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      ) : null}
    </Paper>
  );
}

export default function CreditCustomerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId } = useParams();
  const [search] = useSearchParams();
  const admin = getStoredAdmin();
  const isSuperAdmin = String(admin?.role || '').toLowerCase() === 'super_admin';

  const parsedCustomerId = Number(customerId || 0);
  const fallbackName = location.state?.customerName || '';

  const [filterFrom, setFilterFrom] = useState(search.get('date_from') || '');
  const [filterTo, setFilterTo] = useState(search.get('date_to') || '');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [history, setHistory] = useState(null);

  const [saleRef, setSaleRef] = useState('');
  const [saleAmt, setSaleAmt] = useState('');
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);
  const [saleMethod, setSaleMethod] = useState('');
  const [savingSale, setSavingSale] = useState(false);

  const [rcvAmt, setRcvAmt] = useState('');
  const [rcvDate, setRcvDate] = useState(new Date().toISOString().split('T')[0]);
  const [rcvNote, setRcvNote] = useState('');
  const [savingSettlement, setSavingSettlement] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [clearingCredit, setClearingCredit] = useState(false);
  const [sendingCust, setSendingCust] = useState(false);
  const [sendingInvId, setSendingInvId] = useState(null);

  const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' });
  const showSnack = (severity, msg) => setSnack({ open: true, severity, msg });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePhase, setDeletePhase] = useState('confirm');
  const [deleteTarget, setDeleteTarget] = useState(null); // { kind, id, label, deleteType }
  const [deleteOtp, setDeleteOtp] = useState('');
  const [deleteToken, setDeleteToken] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState('');

  const loadHistory = useCallback(() => {
    if (!parsedCustomerId) {
      setLoading(false);
      setError('Invalid customer id');
      return Promise.resolve();
    }

    setLoading(true);
    setError('');

    const params = new URLSearchParams();
    params.append('customer_id', String(parsedCustomerId));
    if (filterFrom) params.append('date_from', filterFrom);
    if (filterTo) params.append('date_to', filterTo);

    return fetch(`${HISTORY_API_URL}?${params.toString()}`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.status === 'ok') {
          setHistory(d);
        } else {
          setHistory(null);
          setError(d?.message || 'Failed to load history');
        }
      })
      .catch(() => {
        setHistory(null);
        setError('Network error while loading customer history');
      })
      .finally(() => setLoading(false));
  }, [parsedCustomerId, filterFrom, filterTo]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const customer = history?.customer || null;
  const unpaidInvoices = useMemo(
    () => (history?.sales || []).filter((s) => isCredit(s.payment_status)),
    [history]
  );
  const summary = history?.summary || {};
  const allSettlements = history?.settlements?.all || [];
  const closureLogs = history?.closure_logs || [];
  const reminders = history?.reminders || [];
  const ledger = history?.ledger || [];
  const openCreditTotal = Number(summary.open_credit_total || 0);
  const openInvoiceCount = Number(summary.open_credit_count || unpaidInvoices.length || 0);
  const receivedOpen = Number(summary.settlement_open_total || 0);
  const openBalance = Number(summary.open_balance || 0);
  const collectionProgress = openCreditTotal > 0
    ? Math.min(100, Math.max(0, (receivedOpen / openCreditTotal) * 100))
    : 0;

  const goBack = () => {
    const q = new URLSearchParams();
    if (filterFrom) q.append('date_from', filterFrom);
    if (filterTo) q.append('date_to', filterTo);
    navigate(`/sales${q.toString() ? `?${q.toString()}` : ''}`);
  };

  const sendSMS = (to, text) =>
    fetch(`${SMS_API_URL}?to=${encodeURIComponent(to)}&text=${encodeURIComponent(text)}`)
      .then((r) => r.text());

  const fetchPaymentLink = async (saleId = null) => {
    const params = new URLSearchParams();
    params.append('customer_id', String(parsedCustomerId));
    if (saleId != null) {
      params.append('sale_id', String(saleId));
    }
    const d = await fetch(`${CREDIT_PAYMENT_LINK_API_URL}?${params.toString()}`)
      .then((r) => r.json())
      .catch(() => ({ status: 'error' }));
    if (d?.status !== 'ok' || !d?.payment_link) {
      throw new Error(d?.message || 'Failed to generate payment link');
    }
    return String(d.payment_link);
  };

  const logReminder = (saleId, message, apiResponse) =>
    fetch(REMINDERS_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'add',
        customer_id: parsedCustomerId,
        sale_id: saleId ?? null,
        channel: 'sms',
        message,
        api_response: apiResponse,
      }),
    }).then((r) => r.json()).catch(() => ({ status: 'error' }));

  const addCreditSale = async () => {
    const amt = parseFloat(saleAmt);
    if (!amt || amt <= 0) {
      showSnack('warning', 'Enter a valid sale amount');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(saleDate)) {
      showSnack('warning', 'Select a valid sale date');
      return;
    }

    setSavingSale(true);
    try {
      const payload = {
        action: 'add',
        order_details: saleRef.trim(),
        amount: amt,
        payment_method: saleMethod,
        payment_status: 'Credit',
        customer: parsedCustomerId,
        created_at: saleDate,
      };

      const d = await fetch(SALES_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then((r) => r.json());

      if (d?.status !== 'success' && d?.status !== 'ok') {
        showSnack('error', d?.message || 'Failed to add credit sale');
        return;
      }

      setSaleRef('');
      setSaleAmt('');
      setSaleMethod('');
      showSnack('success', 'Credit sale added');
      await loadHistory();
    } catch {
      showSnack('error', 'Network error while adding sale');
    } finally {
      setSavingSale(false);
    }
  };

  const addSettlement = async () => {
    const amt = parseFloat(rcvAmt);
    if (!amt || amt <= 0) {
      showSnack('warning', 'Enter a valid amount');
      return;
    }

    setSavingSettlement(true);
    try {
      const res = await fetch(SETTLEMENTS_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: parsedCustomerId,
          amount: amt,
          pay_date: rcvDate,
          note: rcvNote,
        }),
      });
      const d = await res.json();

      if (d.status !== 'ok') {
        showSnack('error', d.message || 'Failed to add settlement');
        return;
      }

      if (d.auto_closed) {
        const c = d.closure || {};
        showSnack(
          'success',
          `Fully settled. Credit Rs ${fmt(c.open_credit_total)}, received Rs ${fmt(c.received_total)}, SMS: ${c.sms_sent ? 'sent' : 'not sent'}`
        );
      } else {
        showSnack('success', 'Settlement saved');
      }

      setRcvAmt('');
      setRcvNote('');
      await loadHistory();
    } catch {
      showSnack('error', 'Network error');
    } finally {
      setSavingSettlement(false);
    }
  };

  const remindCustomer = async () => {
    if (!customer?.phone) {
      showSnack('warning', 'No phone number for this customer');
      return;
    }
    setSendingCust(true);
    try {
      const paymentLink = await fetchPaymentLink();
      const msg =
        `Dear ${customer.name}, your outstanding balance is Rs.${fmt(summary.open_balance)} ` +
        `for ${summary.open_credit_count || unpaidInvoices.length} invoice(s). Please settle at your earliest convenience.\n` +
        `Pay online securely:\n${paymentLink}\n` +
        `Thank you - ${PAYMENT_ACCOUNT_NAME}!`;
      const resp = await sendSMS(customer.phone, msg);
      await logReminder(null, msg, resp);
      showSnack('success', 'Customer reminder sent');
      await loadHistory();
    } catch {
      showSnack('error', 'Failed to send reminder');
    } finally {
      setSendingCust(false);
    }
  };

  const remindInvoice = async (inv) => {
    if (!customer?.phone) {
      showSnack('warning', 'No phone number for this customer');
      return;
    }
    setSendingInvId(inv.id);
    try {
      const paymentLink = await fetchPaymentLink(inv.id);
      const invNo = displayInvoiceRef(inv.order_details);
      const invDate = (inv.created_at || '').split('T')[0];
      const msg =
        `Dear ${customer.name}, a kind reminder to clear Rs.${fmt(inv.amount)} ` +
        `(Invoice #${invNo}, ${invDate}).\n` +
        `Pay online securely:\n${paymentLink}\n` +
        `Please note: invoice no. ${invNo}.`;
      const resp = await sendSMS(customer.phone, msg);
      await logReminder(inv.id, msg, resp);
      showSnack('success', `Invoice reminder sent (${invNo})`);
      await loadHistory();
    } catch {
      showSnack('error', 'Failed to send invoice reminder');
    } finally {
      setSendingInvId(null);
    }
  };

  const markAllAsPaid = async () => {
    if (!unpaidInvoices.length) {
      showSnack('info', 'No unpaid invoices');
      return;
    }
    setMarkingPaid(true);
    try {
      await Promise.all(unpaidInvoices.map((inv) => {
        const payload = {
          action: 'update',
          id: inv.id,
          order_details: inv.order_details,
          amount: inv.amount,
          payment_method: inv.payment_method || '',
          payment_status: 'paid',
          customer: parsedCustomerId,
          created_at: (inv.created_at || '').split('T')[0],
        };
        return fetch(SALES_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).then((r) => r.json());
      }));

      const closeRes = await fetch(CLOSE_CYCLE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: parsedCustomerId }),
      }).then((r) => r.json()).catch(() => ({ status: 'error' }));

      await loadHistory();
      if (closeRes?.status === 'ok') {
        showSnack(
          'success',
          `Invoices marked paid and receipts closed (${closeRes.closed_count || 0}). SMS: ${closeRes.sms_sent ? 'sent' : 'not sent'}`
        );
      } else {
        showSnack('warning', 'Invoices marked paid, but closing receipts failed');
      }
    } catch {
      showSnack('error', 'Failed to mark all as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  const clearCreditData = async () => {
    if (!customer) return;

    const ok = window.confirm(
      `Clear ALL credit-related data for ${customer.name}?\n\n` +
      `This will:\n` +
      `- mark credit invoices as paid\n` +
      `- delete settlements (open + closed)\n` +
      `- delete reminders\n` +
      `- delete settlement closure logs\n\n` +
      `This cannot be undone.`
    );
    if (!ok) return;

    setClearingCredit(true);
    try {
      const d = await fetch(CLEAR_CREDIT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: parsedCustomerId,
          confirm_clear: true,
        }),
      }).then((r) => r.json());

      if (d?.status !== 'ok') {
        showSnack('error', d?.message || 'Failed to clear credit data');
        return;
      }

      const c = d.counts || {};
      showSnack(
        'success',
        `Cleared. Invoices marked paid: ${c.marked_paid_credit_invoices || 0}, settlements deleted: ${c.deleted_settlements || 0}, reminders deleted: ${c.deleted_reminders || 0}`
      );
      await loadHistory();
    } catch {
      showSnack('error', 'Network error while clearing data');
    } finally {
      setClearingCredit(false);
    }
  };

  const closeDeleteDialog = () => {
    if (deleteBusy) return;
    setDeleteDialogOpen(false);
    setDeletePhase('confirm');
    setDeleteTarget(null);
    setDeleteOtp('');
    setDeleteToken('');
    setDeleteErr('');
  };

  const openDeleteDialog = (kind, row) => {
    if (!isSuperAdmin) {
      showSnack('warning', 'Only super admin can delete credit sales/settlements');
      return;
    }
    const id = Number(row?.id || 0);
    if (!id) return;

    const label = kind === 'sale'
      ? `Invoice ${displayInvoiceRef(row?.order_details)}`
      : 'Settlement record';
    const deleteType = kind === 'sale' ? 'credit_sale_delete' : 'credit_settlement_delete';

    setDeleteTarget({ kind, id, label, deleteType });
    setDeleteDialogOpen(true);
    setDeletePhase('confirm');
    setDeleteOtp('');
    setDeleteToken('');
    setDeleteErr('');
  };

  const sendDeleteOtp = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteErr('');
    try {
      const branchForRef = Number(getSelectedBranchId() || admin?.active_branch_id || 0);
      const deleteRef = `${branchForRef}:${deleteTarget.id}`;
      const details = `${customer?.name || fallbackName || 'Customer'}\n${deleteTarget.label}`;

      const d = await fetch(OTP_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step: 'send',
          delete_type: deleteTarget.deleteType,
          delete_ref: deleteRef,
          details,
        }),
      }).then((r) => r.json());

      if (d?.status !== 'ok' || !d?.token) {
        setDeleteErr(d?.message || 'Failed to send OTP');
        return;
      }

      setDeleteToken(String(d.token));
      setDeletePhase('otp');
      if (d?.debug_otp) {
        showSnack('info', `Debug OTP: ${d.debug_otp}`);
      }
    } catch {
      setDeleteErr('Network error while sending OTP');
    } finally {
      setDeleteBusy(false);
    }
  };

  const confirmDeleteWithOtp = async () => {
    if (!deleteTarget) return;
    if (!deleteToken) {
      setDeleteErr('Missing OTP token. Send OTP again.');
      return;
    }
    if (!/^\d{4}$/.test(String(deleteOtp || '').trim())) {
      setDeleteErr('Enter a valid 4-digit OTP');
      return;
    }

    setDeleteBusy(true);
    setDeleteErr('');
    try {
      const endpoint = deleteTarget.kind === 'sale' ? SALES_API_URL : SETTLEMENTS_API_URL;
      const payload = deleteTarget.kind === 'sale'
        ? {
          action: 'delete_credit_sale',
          id: deleteTarget.id,
          customer_id: parsedCustomerId,
          otp_token: deleteToken,
          otp: deleteOtp,
        }
        : {
          action: 'delete',
          id: deleteTarget.id,
          customer_id: parsedCustomerId,
          otp_token: deleteToken,
          otp: deleteOtp,
        };

      const d = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then((r) => r.json());

      if (d?.status !== 'ok') {
        setDeleteErr(d?.message || 'Delete failed');
        return;
      }

      showSnack(
        'success',
        deleteTarget.kind === 'sale'
          ? 'Credit sale deleted'
          : 'Settlement deleted'
      );

      setDeleteDialogOpen(false);
      setDeletePhase('confirm');
      setDeleteTarget(null);
      setDeleteOtp('');
      setDeleteToken('');
      setDeleteErr('');
      await loadHistory();
    } catch {
      setDeleteErr('Network error while deleting');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.5 },
        background: 'linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%)',
        minHeight: '100%',
      }}
    >
      <Paper
        sx={{
          p: { xs: 1.5, md: 2.5 },
          mb: 2,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #14345f 0%, #1d4f8c 60%, #2e6aad 100%)',
          color: '#fff',
          boxShadow: '0 16px 30px rgba(8,34,63,0.28)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
          <Stack spacing={1.2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={goBack}
                variant="contained"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.16)',
                  color: '#fff',
                  backdropFilter: 'blur(4px)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.24)' },
                }}
              >
                Back To Credit List
              </Button>
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {customer?.name || fallbackName || 'Customer Credit Profile'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {customer?.phone || 'No phone number available'}
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField
              label="From"
              type="date"
              size="small"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150, bgcolor: '#fff', borderRadius: 1 }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150, bgcolor: '#fff', borderRadius: 1 }}
            />
            <Button startIcon={<RefreshIcon />} variant="contained" onClick={loadHistory} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ mt: 2.2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.6 }}>
            <Typography variant="body2" sx={{ opacity: 0.95 }}>
              Open-cycle collection progress
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {collectionProgress.toFixed(1)}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={collectionProgress}
            sx={{
              height: 9,
              borderRadius: 6,
              bgcolor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': { borderRadius: 6, bgcolor: '#7fe18e' },
            }}
          />
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <StatCard
              label="Open Credit"
              value={`Rs ${fmt(openCreditTotal)}`}
              color="warning.main"
              helper={`${openInvoiceCount} invoice(s)`}
            />
            <StatCard
              label="Received (Open Cycle)"
              value={`Rs ${fmt(receivedOpen)}`}
              color="success.main"
              helper={`${fmt(collectionProgress)}% collected`}
            />
            <StatCard
              label="Balance"
              value={`Rs ${fmt(openBalance)}`}
              color={openBalance > 0 ? 'error.main' : 'success.main'}
              helper={openBalance > 0 ? 'Pending amount' : 'Fully collected'}
            />
            <StatCard
              label="Settled Cycles"
              value={String(summary.auto_closure_count || 0)}
              color="primary.main"
              helper={`${closureLogs.length} closure log(s)`}
            />
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
              mb: 2,
            }}
          >
            <Paper sx={{ p: 2.2, borderRadius: 2.5 }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>Add Credit Sale</Typography>
              <Stack spacing={1.2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                  <TextField
                    label="Date"
                    type="date"
                    size="small"
                    value={saleDate}
                    onChange={(e) => setSaleDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="Amount"
                    type="number"
                    size="small"
                    value={saleAmt}
                    onChange={(e) => setSaleAmt(e.target.value)}
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Invoice / Order Ref"
                  size="small"
                  value={saleRef}
                  onChange={(e) => setSaleRef(e.target.value)}
                  placeholder="Example: 14-2210"
                  fullWidth
                />
                <TextField
                  select
                  label="Payment Method"
                  size="small"
                  value={saleMethod}
                  onChange={(e) => setSaleMethod(e.target.value)}
                  fullWidth
                >
                  {paymentMethodOptions.map((opt) => (
                    <MenuItem key={opt.value || 'empty'} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="contained" onClick={addCreditSale} disabled={savingSale}>
                  {savingSale ? 'Saving...' : 'Add Credit Sale'}
                </Button>
              </Stack>
            </Paper>

            <Paper sx={{ p: 2.2, borderRadius: 2.5 }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>Receive Amount</Typography>
              <Stack spacing={1.2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                  <TextField
                    label="Amount"
                    type="number"
                    size="small"
                    value={rcvAmt}
                    onChange={(e) => setRcvAmt(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Date"
                    type="date"
                    size="small"
                    value={rcvDate}
                    onChange={(e) => setRcvDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Note"
                  size="small"
                  value={rcvNote}
                  onChange={(e) => setRcvNote(e.target.value)}
                  fullWidth
                />
                <Button variant="contained" onClick={addSettlement} disabled={savingSettlement}>
                  {savingSettlement ? 'Saving...' : 'Add Settlement'}
                </Button>
              </Stack>
            </Paper>
          </Box>

          <Paper sx={{ p: 2, mb: 2, borderRadius: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Quick Actions
            </Typography>
            {!isSuperAdmin ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Delete actions are available only for super admin with OTP verification.
              </Typography>
            ) : null}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} flexWrap="wrap">
              <Button
                startIcon={sendingCust ? <CircularProgress size={16} /> : <NotificationsActiveIcon />}
                variant="outlined"
                onClick={remindCustomer}
                disabled={sendingCust}
              >
                Remind Customer
              </Button>
              <Button
                color="success"
                startIcon={markingPaid ? <CircularProgress size={16} /> : <DoneAllIcon />}
                variant="contained"
                onClick={markAllAsPaid}
                disabled={markingPaid || unpaidInvoices.length === 0}
              >
                Mark All As Paid
              </Button>
              <Button color="error" variant="outlined" onClick={clearCreditData} disabled={clearingCredit}>
                {clearingCredit ? 'Clearing...' : 'Clear Credit Data'}
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mb: 2, borderRadius: 2.5 }}>
            <Typography variant="h6">Unpaid Invoices</Typography>
            <Divider sx={{ my: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={64}>No.</TableCell>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unpaidInvoices.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center">No unpaid invoices</TableCell></TableRow>
                ) : unpaidInvoices.map((inv, index) => (
                  <TableRow key={inv.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{displayInvoiceRef(inv.order_details)}</TableCell>
                    <TableCell>{(inv.created_at || '').split('T')[0]}</TableCell>
                    <TableCell align="right">Rs {fmt(inv.amount)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.6} justifyContent="center">
                        <Tooltip title="Send invoice reminder">
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => remindInvoice(inv)}
                              disabled={sendingInvId === inv.id}
                            >
                              {sendingInvId === inv.id ? <CircularProgress size={16} /> : <NotificationImportantIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {isSuperAdmin ? (
                          <Tooltip title="Delete credit sale (OTP required)">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openDeleteDialog('sale', inv)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 2, mb: 2, borderRadius: 2.5 }}>
            <Typography variant="h6">Settlement History (All)</Typography>
            <Divider sx={{ my: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={64}>No.</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allSettlements.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center">No settlements</TableCell></TableRow>
                ) : allSettlements.map((s, index) => (
                  <TableRow key={s.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{s.pay_date}</TableCell>
                    <TableCell align="right">Rs {fmt(s.amount)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={Number(s.closed) === 1 ? 'success' : 'warning'}
                        label={Number(s.closed) === 1 ? 'Closed Cycle' : 'Open Cycle'}
                      />
                    </TableCell>
                    <TableCell>{s.note || '-'}</TableCell>
                    <TableCell align="center">
                      {isSuperAdmin ? (
                        <Tooltip title="Delete settlement (OTP required)">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteDialog('settlement', s)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
            <Paper sx={{ p: 2, flex: 1, borderRadius: 2.5 }}>
              <Typography variant="h6">Closure Logs</Typography>
              <Divider sx={{ my: 1 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={64}>No.</TableCell>
                    <TableCell>Closed At</TableCell>
                    <TableCell align="right">Credit</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell>SMS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {closureLogs.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center">No closure logs</TableCell></TableRow>
                  ) : closureLogs.map((c, index) => (
                    <TableRow key={c.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{c.closed_at}</TableCell>
                      <TableCell align="right">Rs {fmt(c.open_credit_total)}</TableCell>
                      <TableCell align="right">Rs {fmt(c.received_total)}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" color={Number(c.sms_sent) === 1 ? 'success' : 'default'} label={Number(c.sms_sent) === 1 ? 'Sent' : 'Not Sent'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <Paper sx={{ p: 2, flex: 1, borderRadius: 2.5 }}>
              <Typography variant="h6">Recent Reminders</Typography>
              <Divider sx={{ my: 1 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={64}>No.</TableCell>
                    <TableCell>Sent At</TableCell>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Channel</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reminders.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center">No reminders</TableCell></TableRow>
                  ) : reminders.slice(0, 20).map((r, index) => (
                    <TableRow key={r.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{r.sent_at}</TableCell>
                      <TableCell>{Number(r.sale_id || 0) > 0 ? 'Invoice reminder' : 'Customer reminder'}</TableCell>
                      <TableCell>{r.channel}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Stack>

          <Paper sx={{ p: 2, mt: 2, borderRadius: 2.5 }}>
            <Typography variant="h6">Ledger Timeline</Typography>
            <Divider sx={{ my: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={64}>No.</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledger.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center">No ledger records</TableCell></TableRow>
                ) : ledger.slice(-100).map((l, idx) => (
                  <TableRow key={`${l.type}-${l.sale_id || l.settlement_id || idx}`}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{l.date}</TableCell>
                    <TableCell>{l.type === 'invoice' ? 'Invoice' : 'Settlement'}</TableCell>
                    <TableCell align="right">Rs {fmt(l.amount)}</TableCell>
                    <TableCell>{l.status || '-'}</TableCell>
                    <TableCell>{l.reference || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          {deleteTarget?.kind === 'sale' ? 'Delete Credit Sale' : 'Delete Settlement'}
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          <Alert severity="warning">
            This action is allowed only for super admin and requires OTP verification.
          </Alert>
          <Typography variant="body2">
            Target: <strong>{deleteTarget?.label || '-'}</strong>
          </Typography>
          <Typography variant="body2">
            Customer: <strong>{customer?.name || fallbackName || 'Customer'}</strong>
          </Typography>
          {deletePhase === 'otp' ? (
            <TextField
              label="Enter OTP"
              size="small"
              value={deleteOtp}
              onChange={(e) => setDeleteOtp(e.target.value.replace(/\D/g, ''))}
              inputProps={{ maxLength: 4, inputMode: 'numeric' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Click "Send OTP" to receive a verification code.
            </Typography>
          )}
          {deleteErr ? <Alert severity="error">{deleteErr}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteBusy}>Cancel</Button>
          {deletePhase === 'confirm' ? (
            <Button variant="contained" onClick={sendDeleteOtp} disabled={deleteBusy || !deleteTarget}>
              {deleteBusy ? 'Sending...' : 'Send OTP'}
            </Button>
          ) : (
            <Button
              color="error"
              variant="contained"
              onClick={confirmDeleteWithOtp}
              disabled={deleteBusy || !deleteToken || !deleteOtp}
            >
              {deleteBusy ? 'Deleting...' : 'Verify OTP & Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
